import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import dayjs from "dayjs";

import SchedulerDialog from "../../Messenger/components/SchedulerDialog";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const RECIPIENT_LABELS = {
  all: "All Attendees",
  incompleteProfile: "Incomplete Profiles",
  present: "Present",
  notPresent: "Not Present",
  presentNotCheckedIn: "Present But Not Checked In",
  checkedIn: "Checked  In Attendees",
  notCheckedIn: "Not Checked In",
  matched: "Have Matches",
  noMatches: "No Matches",
  needToSign: "Need to Sign Waiver",
  custom: "Custom",
};

const ScheduleMsgDialog = ({ open, close, pairingEventId }) => {
  const [messages, setMessages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editSendAt, setEditSendAt] = useState(null);

  useEffect(() => {
    if (open) {
      getMessages();
    }
  }, [open]);

  const getMessages = async () => {
    const url = `templates/scheduledmessages/?pairingEventId=${pairingEventId}`;
    const result = await request(url);
    if (result.success) {
      setMessages(result.data);
    }
  };

  const handleDelete = async (id) => {
    const result = await request(`templates/deletescheduledmessage`, "post", {
      id,
    });
    if (result.success) {
      setMessages((prev) => prev.filter((msg) => msg._id !== id));
    }
  };

  const handleEdit = (msg) => {
    setEditingId(msg._id);
    setEditSendAt(dayjs(msg.sendAt));
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditSendAt(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editSendAt) return;
    const result = await request("templates/editscheduledmessage", "post", {
      id: editingId,
      sendAt: editSendAt,
    });
    if (result.success) {
      const newSendAt = editSendAt.toISOString();
      setMessages((prev) =>
        prev.map((m) => (m._id === editingId ? { ...m, sendAt: newSendAt } : m))
      );
      toast.success("Scheduled message updated");
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth>
      <DialogTitle>Scheduled Messages</DialogTitle>
      <DialogContent>
        {messages.length === 0 ? (
          <DialogContentText>No scheduled messages found.</DialogContentText>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold" }}>Template</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Send At</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Recipients</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Channel</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((msg) => (
                <TableRow key={msg._id}>
                  <TableCell>{msg.template}</TableCell>
                  <TableCell>
                    {dayjs(msg.sendAt).local().format("MM/DD/YY hh:mm A")}
                  </TableCell>
                  <TableCell>{RECIPIENT_LABELS[msg.recipientFilter] ?? msg.recipientFilter}</TableCell>
                  <TableCell>{msg.channel}</TableCell>
                  <TableCell>
                    {msg.status}
                    {msg.status === "failed" && msg.note && (
                      <div
                        style={{
                          color: "gray",
                          fontSize: "0.8em",
                          marginTop: 4,
                        }}
                      >
                        {msg.note}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEdit(msg)}
                      size="small"
                      disabled={msg.status !== "pending"}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(msg._id)}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
      <SchedulerDialog
        open={Boolean(editingId)}
        close={closeEdit}
        handleSend={handleSaveEdit}
        sendAt={editSendAt}
        setSendAt={setEditSendAt}
        title="Edit Scheduled Message"
        confirmLabel="Save"
      />
    </Dialog>
  );
};

export default ScheduleMsgDialog;
